import { Fragment } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { BlockBreadcrumb } from "./documentEditor";

interface BlockBreadcrumbNavProps {
  /** Enclosing selectable blocks, outermost first. */
  ancestors: BlockBreadcrumb[];
  /** Label of the current block, shown as the active crumb. */
  current: string;
  /** Nested repeater blocks to drill into. */
  nested: BlockBreadcrumb[];
  /** Selects a block by node id. */
  onSelectNode: (nodeId: string) => void;
}

/**
 * Breadcrumb for moving the selection up to an enclosing block or down into a
 * nested repeater. Both directions cover blocks a canvas click cannot reach: a
 * container is covered edge to edge by the children that own its pixels, and a
 * nested repeater is covered by its own stamped instances. Ancestors read
 * muted and clickable, the current block is the single filled crumb, so the
 * chain reads as "inside this section, editing this block".
 * @param ancestors - Enclosing blocks, outermost first
 * @param current - Label of the current block
 * @param nested - Nested repeater blocks
 * @param onSelectNode - Selects a block by node id
 */
export function BlockBreadcrumbNav({
  ancestors,
  current,
  nested,
  onSelectNode,
}: BlockBreadcrumbNavProps) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <Breadcrumb className="min-w-0 text-xs">
        <BreadcrumbList className="flex-wrap gap-1">
          {ancestors.map((ancestor) => (
            <Fragment key={ancestor.nodeId}>
              <BreadcrumbItem>
                <button
                  type="button"
                  className="rounded px-1 py-0.5 transition-colors hover:bg-muted hover:text-foreground"
                  title={`Select ${ancestor.label}`}
                  onClick={() => onSelectNode(ancestor.nodeId)}
                >
                  {ancestor.label}
                </button>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </Fragment>
          ))}
          <BreadcrumbItem>
            <BreadcrumbPage className="rounded bg-muted px-1.5 py-0.5 font-medium">
              {current}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      {nested.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Nested:</span>
          {nested.map((child) => (
            <button
              key={child.nodeId}
              type="button"
              className="rounded border px-1 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => onSelectNode(child.nodeId)}
            >
              {child.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
